import { useEffect, useState } from 'react'
import api from '../services/api'

// Motivos da divergência de média (Combustível → Parâmetros): lista do "Lançamento está correto" da Análise da média

interface Motivo {
  id: number
  descricao: string
  ativo: 'S' | 'N'
  ordem: number
}

const NOVO_MOTIVO: Motivo = { id: 0, descricao: '', ativo: 'S', ordem: 0 }

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

export default function MotivosDivergenciaMedia() {
  const [lista, setLista] = useState<Motivo[]>([])
  const [busca, setBusca] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [erroLista, setErroLista] = useState('')
  const [selecionado, setSelecionado] = useState<Motivo | null>(null)
  const [modoCriar, setModoCriar] = useState(false)
  const [form, setForm] = useState<Motivo | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msgSalvar, setMsgSalvar] = useState<{ texto: string; ok: boolean } | null>(null)

  async function carregarLista() {
    setCarregando(true); setErroLista('')
    try {
      const r = await api.get('/combustivel/motivos-divergencia')
      setLista(r.data)
      return r.data as Motivo[]
    } catch (e: any) {
      setErroLista(e?.response?.data?.error || 'Erro ao carregar os motivos.')
      return []
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => { carregarLista() }, [])

  const termo = busca.trim().toLowerCase()
  const filtrados = lista.filter(m => !termo || m.descricao.toLowerCase().includes(termo) || String(m.id).includes(termo))

  function selecionar(m: Motivo) {
    setModoCriar(false); setMsgSalvar(null)
    setSelecionado(m); setForm({ ...m })
  }

  function novo() {
    setSelecionado(null); setModoCriar(true); setMsgSalvar(null)
    // Sugere a próxima ordem, depois do último motivo
    setForm({ ...NOVO_MOTIVO, ordem: Math.max(0, ...lista.map(m => m.ordem)) + 1 })
  }

  function setCampo<K extends keyof Motivo>(campo: K, valor: Motivo[K]) {
    setForm(f => f && { ...f, [campo]: valor })
  }

  async function salvar() {
    if (!form) return
    setSalvando(true); setMsgSalvar(null)
    try {
      let id = selecionado?.id ?? 0
      if (modoCriar) {
        const r = await api.post('/combustivel/motivos-divergencia', form)
        id = r.data.id
      } else if (selecionado) {
        await api.put(`/combustivel/motivos-divergencia/${selecionado.id}`, form)
      }
      const nova = await carregarLista()
      const atual = nova.find(m => m.id === id)
      if (atual) selecionar(atual)
      setMsgSalvar({ texto: modoCriar ? 'Motivo incluído com sucesso.' : 'Alterações salvas com sucesso.', ok: true })
    } catch (e: any) {
      setMsgSalvar({ texto: e?.response?.data?.error || 'Erro ao salvar.', ok: false })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Lista */}
      <div className="card" style={{ width: 380, flexShrink: 0 }}>
        <h3 style={{
          fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8',
          paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
        }}>
          Motivos de Divergência de Média
        </h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', alignItems: 'center' }}>
          <div className="field" style={{ flex: 1 }}>
            <input type="text" placeholder="Código ou descrição..." value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={novo} disabled={!!erroLista} title="Novo motivo">+ Novo</button>
        </div>

        {carregando && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
        {erroLista && <p style={{ color: '#991b1b', fontSize: '0.85rem' }}>{erroLista}</p>}
        {!carregando && !erroLista && filtrados.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum motivo encontrado.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
          {filtrados.map(m => {
            const ativo = selecionado?.id === m.id
            const inativo = m.ativo !== 'S'
            return (
              <button
                key={m.id}
                onClick={() => selecionar(m)}
                style={{
                  textAlign: 'left', border: '1px solid #e5e7eb', borderLeft: `4px solid ${inativo ? '#cbd5e1' : '#1a6ea8'}`,
                  borderRadius: 6, padding: '8px 10px', cursor: 'pointer', opacity: inativo ? 0.65 : 1,
                  background: ativo ? '#eff6ff' : '#f8fafc',
                  borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{m.id}</span>
                  <span style={{ fontSize: '0.72rem', color: inativo ? '#b45309' : '#9ca3af' }}>{inativo ? 'Inativo' : `Ordem ${m.ordem}`}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 3 }}>{m.descricao}</div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Detalhe */}
      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        {!selecionado && !modoCriar && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            Selecione um motivo na lista ao lado, ou clique em "+ Novo". Os motivos ativos aparecem no "Lançamento está correto" da Análise da média, na ordem informada.
          </p>
        )}

        {(selecionado || modoCriar) && form && (
          <>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8' }}>
                {modoCriar ? 'Novo Motivo' : `${selecionado?.id} — ${selecionado?.descricao}`}
              </h3>
            </div>

            <div style={{
              background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
              borderRadius: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.55rem',
              display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end',
            }}>
              <Campo label="Código" width={80}>
                <input type="text" value={modoCriar ? '' : selecionado?.id ?? ''} placeholder={modoCriar ? 'Auto' : ''} readOnly disabled />
              </Campo>
              <Campo label="Descrição" width={620}>
                <input type="text" maxLength={120} value={form.descricao} onChange={e => setCampo('descricao', e.target.value)} />
              </Campo>
              <Campo label="Ordem" width={80}>
                <input type="number" value={form.ordem} onChange={e => setCampo('ordem', e.target.value === '' ? 0 : Number(e.target.value))} />
              </Campo>
              <Campo label="Ativo" width={90}>
                <select value={form.ativo} onChange={e => setCampo('ativo', e.target.value as Motivo['ativo'])}>
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </select>
              </Campo>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : modoCriar ? 'Incluir' : 'Salvar alterações'}
              </button>
              {msgSalvar && (
                <span style={{
                  fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                  background: msgSalvar.ok ? '#d1fae5' : '#fee2e2',
                  color: msgSalvar.ok ? '#065f46' : '#991b1b',
                }}>
                  {msgSalvar.texto}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
